/**
 * CompanyComparisonTables — tabbed multi-year financial summary tables.
 *
 * Tab 1 "Detailed Comparison": ROIC, WACC, D/E Ratio, Total Debt, Shareholders Equity
 * Tab 2 "Valuation Metrics":   EV, EV/EBITDA, EV from EBIT, EV from Growth, EV Market Exp.
 *
 * Rows are sorted most-recent first; latest year is highlighted.
 */
import { useState } from 'react';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { formatFiscalYear, formatCurrencyValue, formatPercent } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function CompanyComparisonTables({ data, region }: Props) {
  const [activeTab, setActiveTab] = useState<'detailed' | 'valuation'>('detailed');

  const rows = [...data].reverse();
  const unit = region?.currency?.unit_abbr ?? '';

  const hasDetailedData = rows.some(
    (d) => d.roic != null || d.wacc != null || d.debt_equity_ratio != null
  );
  const hasValuationData = rows.some(
    (d) => d.enterprise_value != null || d.ev_ebitda != null
  );

  if (!hasDetailedData && !hasValuationData) return null;

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold text-neutral-800">
          Multi-Year Financial Summary ({unit})
        </h3>
        <p className="text-xs text-neutral-500 mt-0.5">
          All available years — most recent first
        </p>
        {/* Tab bar */}
        <div className="flex gap-1 mt-3 border-b border-neutral-200 -mx-0">
          <button
            onClick={() => setActiveTab('detailed')}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'detailed'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            Detailed Comparison
          </button>
          <button
            onClick={() => setActiveTab('valuation')}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'valuation'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            Valuation Metrics Details
          </button>
        </div>
      </CardHeader>

      <CardBody className="p-0">
        <div className="overflow-x-auto">
          {activeTab === 'detailed' ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                  <th className="text-left py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider w-28">
                    Year
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    ROIC
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    WACC
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    Spread
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    D/E Ratio
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    Total Debt
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    Shareholders Equity
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d, i) => {
                  const spread =
                    d.roic != null && d.wacc != null ? (d.roic - d.wacc) * 100 : null;
                  return (
                    <tr
                      key={d.year}
                      className={`border-b border-neutral-100 hover:bg-blue-50/30 transition-colors ${
                        i === 0 ? 'bg-blue-50/20' : ''
                      }`}
                    >
                      <td className="py-3 px-4 font-semibold text-neutral-800 tabular-nums">
                        {formatFiscalYear(d.year, region)}
                        {i === 0 && (
                          <span className="ml-1.5 text-[9px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full font-bold">
                            Latest
                          </span>
                        )}
                      </td>
                      <td
                        className={`py-3 px-4 text-right tabular-nums font-semibold ${
                          spread != null && spread >= 0 ? 'text-emerald-700' : 'text-red-600'
                        }`}
                      >
                        {d.roic != null ? formatPercent(d.roic) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-neutral-700">
                        {d.wacc != null ? formatPercent(d.wacc) : '—'}
                      </td>
                      <td
                        className={`py-3 px-4 text-right tabular-nums font-semibold text-xs ${
                          spread != null
                            ? spread >= 0
                              ? 'text-emerald-600'
                              : 'text-red-500'
                            : 'text-neutral-400'
                        }`}
                      >
                        {spread != null
                          ? `${spread >= 0 ? '+' : ''}${spread.toFixed(1)}pp`
                          : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-neutral-700">
                        {d.debt_equity_ratio != null ? d.debt_equity_ratio.toFixed(2) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-neutral-700">
                        {d.total_debt != null ? formatCurrencyValue(d.total_debt, region) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-neutral-700">
                        {d.shareholders_funds != null
                          ? formatCurrencyValue(d.shareholders_funds, region)
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-neutral-200 bg-neutral-50">
                  <th className="text-left py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider w-28">
                    Year
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    EV
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    EV/EBITDA
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    EV from EBIT
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    EV from Growth
                  </th>
                  <th className="text-right py-3 px-4 text-neutral-500 font-semibold uppercase tracking-wider">
                    EV Market Exp.
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d, i) => (
                  <tr
                    key={d.year}
                    className={`border-b border-neutral-100 hover:bg-blue-50/30 transition-colors ${
                      i === 0 ? 'bg-blue-50/20' : ''
                    }`}
                  >
                    <td className="py-3 px-4 font-semibold text-neutral-800 tabular-nums">
                      {formatFiscalYear(d.year, region)}
                      {i === 0 && (
                        <span className="ml-1.5 text-[9px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded-full font-bold">
                          Latest
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums font-semibold text-neutral-900">
                      {d.enterprise_value != null
                        ? formatCurrencyValue(d.enterprise_value, region)
                        : '—'}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-neutral-700">
                      {d.ev_ebitda != null ? `${d.ev_ebitda.toFixed(1)}x` : '—'}
                    </td>
                    {/* Echo the EV_SEGMENT purple ramp (dark→light): current ops,
                        future growth, market expectation. */}
                    <td className="py-3 px-4 text-right tabular-nums text-secondary-800">
                      {d.ev_from_ebit != null
                        ? formatCurrencyValue(d.ev_from_ebit, region)
                        : '—'}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-secondary-600">
                      {d.ev_from_future_growth != null
                        ? formatCurrencyValue(d.ev_from_future_growth, region)
                        : '—'}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-secondary-400">
                      {d.ev_from_future_market_expectation != null
                        ? formatCurrencyValue(d.ev_from_future_market_expectation, region)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
